import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import LoadingButton from '@mui/lab/LoadingButton';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useMediaDevices } from 'react-media-devices';
import { analyzePhoto } from '../../../../api';
import { MealType } from '../../../../common/commonTypes';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import colors from 'tailwindcss/colors';

const constraints: MediaStreamConstraints = {
    video: true,
    audio: false,
};

type CameraModalType = {
    onCancel: () => void;
    onSuccess: (mealToSave: MealType[]) => void;
};

const CameraModal = (props: CameraModalType) => {
    const { onCancel, onSuccess } = props;

    const { t, i18n } = useTranslation();

    const [deviceId, setDeviceId] = useState('');
    const [loading, setLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [streamActive, setStreamActive] = useState(false);

    // Retrieve all devices
    const { devices } = useMediaDevices({ constraints });

    // Memoize the filtering of video devices
    const availVideoDevices = useMemo(() => {
        const seenGroups = new Set();
        return (devices ?? []).filter((device) => {
            if (
                device.kind !== 'videoinput' ||
                !device.deviceId ||
                !device.groupId ||
                device.label.toLowerCase().includes('front') ||
                device.label.toLowerCase().includes('wide') ||
                device.label.toLowerCase().includes('telephoto') ||
                device.label.toLowerCase().includes('dual') ||
                device.label.toLowerCase().includes('triple') ||
                device.label.toLowerCase().includes('camera 2, facing back')
            ) {
                return false;
            }
            // If we've already seen this groupId, skip this device.
            if (seenGroups.has(device.groupId)) {
                return false;
            }
            // Mark this groupId as seen and include this device.
            seenGroups.add(device.groupId);
            return true;
        });
    }, [devices]);

    // console.log('%c availVideoDevices >>> ', 'background: #333; color: #bada55', availVideoDevices);
    // alert(JSON.stringify(availVideoDevices));

    const handleNotFound = () => {
        setLoading(false);
        // @ts-ignore
        // setAlertMessage(t('product_not_found'));
        setAlertMessage("Can't recognize a meal on the photo");
        setTimeout(() => {
            setAlertMessage('');
        }, 3000);
    };

    // Set the last available device if not set yet
    useEffect(() => {
        if (!deviceId && availVideoDevices.length > 0) {
            const lastDevice = availVideoDevices[availVideoDevices.length - 1];
            setDeviceId(lastDevice.deviceId);
        }
    }, [deviceId, availVideoDevices]);

    const handleCancel = () => {
        stopCamera();
        onCancel();
    };

    // Start camera
    const startCamera = async () => {
        setAlertMessage('');
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'environment' },
                audio: false,
            });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = async () => {
                    await videoRef.current?.play();
                    setStreamActive(true);
                };
            }
            setStreamActive(true);
        } catch (e) {
            if (JSON.stringify(e) !== '{}') {
                // setAlertMessage('Camera permission denied or not available.');
                setAlertMessage(JSON.stringify(e));
            }
        }
    };

    // Stop camera (optional cleanup)
    const stopCamera = () => {
        const stream = videoRef.current?.srcObject as MediaStream | null;
        stream?.getTracks().forEach((t) => t.stop());
        setStreamActive(false);
    };

    useEffect(() => {
        if (deviceId) {
            startCamera();
        }

        return () => stopCamera(); // cleanup on unmount
    }, [deviceId]);

    // Capture a frame to a blob
    const takePhoto = async () => {
        window.gtag?.('event', 'took photo', {});

        setAlertMessage('');
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;

        const width = video.videoWidth;
        const height = video.videoHeight;
        if (!width || !height) {
            setAlertMessage('Camera not ready yet.');
            return;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.drawImage(video, 0, 0, width, height);

        // convert to blob (jpeg keeps size reasonable)
        const blob: Blob | null = await new Promise((resolve) =>
            canvas.toBlob(resolve, 'image/jpeg', 0.9)
        );

        if (!blob) {
            setAlertMessage('Could not capture image.');
            return;
        }

        // send to BE
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('image', blob, 'capture.jpg');
            formData.append('langCode', i18n.language);

            const response = await analyzePhoto(formData);

            console.log('%c >>> ', 'background: #333; color: #bada55', response.data);
            onSuccess(response.data);
        } catch (error: any) {
            if (error.status === 404) {
                handleNotFound();
            } else {
                console.error('Failed to send photo for analysis:', error);
                alert('Failed to send photo for analysis.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    handleCancel();
                }
            }}
        >
            <img src="/calPal_fork_transp_s_cut.png" className="absolute bottom-0 left-0" />

            <DialogTitle>{t('take_photo')}</DialogTitle>
            <DialogContent>
                <div className="relative">
                    {alertMessage && (
                        <Alert severity="warning" className="mb-2">
                            {alertMessage}
                        </Alert>
                    )}

                    <video
                        ref={videoRef}
                        playsInline
                        muted
                        style={{ width: '100%', background: colors.gray[100] }}
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />

                    <div className="text-center text-xs text-gray-700 my-2">
                        {t('take_photo_instructions')}
                    </div>

                    {availVideoDevices.length > 1 && (
                        <ToggleButtonGroup
                            size="small"
                            color="primary"
                            value={deviceId}
                            exclusive
                            onChange={(e, newId) => setDeviceId(newId)}
                            orientation="vertical"
                        >
                            {availVideoDevices.map((device, idx) => (
                                <ToggleButton key={device.deviceId} value={device.deviceId}>
                                    {/*<PhotoCameraIcon /> Camera {idx + 1}*/}
                                    <PhotoCameraIcon />
                                    {device.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    )}

                    {loading && (
                        <div className="absolute top-0 bottom-0 left-0 right-0 bg-neutral-100 rounded flex flex-col items-center justify-center">
                            <div className="mb-2 text-center text-gray-400">
                                <CircularProgress size={30} sx={{ color: 'inherit' }} />
                            </div>
                            <div className="text-center text-xs text-gray-500">
                                {t('analyzing')}
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>

            <DialogActions>
                <LoadingButton onClick={handleCancel}>{t('MODAL_cancel')}</LoadingButton>
                <LoadingButton
                    loading={!streamActive || loading}
                    variant="contained"
                    onClick={takePhoto}
                >
                    <PhotoCameraIcon className="mr-2" />
                    {t('MODAL_send')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default CameraModal;
